import React from "react";
import { Mail, Phone, MapPin, Instagram, Linkedin, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useSettings } from "../hooks/useSettings";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "./Logo";
import MapEmbed from "./MapEmbed";

const SERVICE_KEYS = [
  { slug: "meta-ads", k: "services_options.meta_ads" },
  { slug: "google-ads", k: "services_options.google_ads" },
  { slug: "seo", k: "services_options.seo" },
  { slug: "creative", k: "services_options.creative" },
  { slug: "social-media", k: "services_options.social_media" },
  { slug: "web-development", k: "services_options.web_development" },
];

const COMPANY_LINKS = [
  { href: "#about", k: "nav.about" },
  { href: "#work", k: "nav.work" },
  { href: "#blog", k: "nav.blog" },
  { href: "#contact", k: "nav.contact" },
];

// SEO — Istanbul districts × agency keywords for Google indexing
const ISTANBUL_SEO_KEYWORDS = [
  // Beşiktaş & çevresi
  "Beşiktaş Reklam Ajansı", "Beşiktaş SEO Ajansı", "Beşiktaş Sosyal Medya Uzmanı",
  "Beşiktaş Dijital Pazarlama", "Beşiktaş Web Tasarım", "Beşiktaş Google Ads",
  "Beşiktaş Meta Ads", "Beşiktaş Performance Marketing",
  "Etiler Reklam Ajansı", "Etiler Web Tasarım Ajansı", "Etiler Dijital Ajans",
  "Levent Reklam Ajansı", "4. Levent Grafik Tasarım", "Levent Dijital Pazarlama",
  "Maslak Reklam Ajansı", "Maslak Sosyal Medya", "Maslak SEO Ajansı",
  "Nişantaşı Dijital Ajans", "Nişantaşı İçerik Ajansı",
  // Anadolu yakası
  "Kadıköy Reklam Ajansı", "Kadıköy Dijital Pazarlama", "Kadıköy SEO",
  "Kadıköy Sosyal Medya Yönetimi", "Kadıköy Web Tasarım",
  "Ataşehir Reklam Ajansı", "Ataşehir Dijital Ajans",
  "Üsküdar Reklam Ajansı", "Üsküdar Sosyal Medya",
  "Maltepe Dijital Pazarlama", "Kartal Reklam Ajansı",
  "Pendik Google Ads Ajansı", "Ümraniye Sosyal Medya Ajansı",
  // Avrupa yakası geniş
  "Şişli Dijital Pazarlama Ajansı", "Şişli SEO Hizmeti",
  "Mecidiyeköy Reklam Ajansı", "Gayrettepe Dijital Ajans",
  "Bakırköy Reklam Ajansı", "Bakırköy Web Tasarım",
  "Bahçelievler Sosyal Medya", "Bağcılar Google Ads",
  "Beyoğlu Dijital Ajans", "Beyoğlu Kreatif Ajans",
  "Fatih Reklam Ajansı", "Sarıyer Dijital Pazarlama",
  "Kağıthane SEO Ajansı", "Eyüpsultan Reklam Ajansı",
  "Gaziosmanpaşa Dijital Pazarlama", "Sultangazi Sosyal Medya",
  "Esenyurt Meta Ads", "Beylikdüzü Reklam Ajansı",
  "Avcılar Google Ads Ajansı", "Büyükçekmece Dijital Ajans",
  "Başakşehir Reklam Ajansı", "Arnavutköy Sosyal Medya",
  "Silivri Dijital Pazarlama", "Çatalca Web Tasarım",
  "Beykoz Reklam Ajansı", "Çekmeköy Dijital Ajans",
  "Sancaktepe Sosyal Medya", "Tuzla Google Ads",
  "Güngören Reklam Ajansı", "Esenler Meta Ads",
  "Bayrampaşa SEO Hizmeti", "Zeytinburnu Dijital Ajans",
  // Trend keyword grupları — İstanbul geneli
  "İstanbul Reklam Ajansı", "İstanbul SEO Ajansı",
  "İstanbul Sosyal Medya Yönetimi", "İstanbul Dijital Pazarlama Ajansı",
  "İstanbul Google Ads Ajansı", "İstanbul Meta Ads Ajansı",
  "İstanbul Web Tasarım Ajansı", "İstanbul Grafik Tasarım Ajansı",
  "İstanbul İçerik Üretimi", "İstanbul Performance Marketing",
  "İstanbul E-ticaret Ajansı", "İstanbul Influencer Marketing",
  "İstanbul Video Prodüksiyon", "İstanbul Marka Stratejisi",
  "İstanbul B2B Dijital Pazarlama", "İstanbul KOBİ Reklam Ajansı",
  "Türkiye'nin En İyi Dijital Ajansı", "En İyi SEO Ajansı İstanbul",
  "Kurumsal Reklam Ajansı İstanbul", "360 Derece Dijital Pazarlama",
];

export default function Footer() {
  const { t, lang } = useLanguage();
  const settings = useSettings();
  const year = new Date().getFullYear();
  const ig = (settings.instagram_handle || "postunhazir").replace(/^@/, "");

  return (
    <footer data-testid="site-footer" className="border-t border-mist bg-cream">
      <div className="container-page py-20">
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-12">
          {/* Brand col */}
          <div className="lg:col-span-4">
            <Logo testId="footer-brand-logo" uniqueId="footer-logo" />
            <p className="mt-4 text-sm text-ink/65 max-w-xs leading-relaxed whitespace-pre-line">
              {t("footer.tagline")}
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { href: "https://www.instagram.com/postunhazir", icon: Instagram, name: "Instagram" },
                { href: "https://www.facebook.com/yastikmedya", icon: Facebook, name: "Facebook" },
                { href: "https://www.linkedin.com/company/yastikmedya", icon: Linkedin, name: "LinkedIn" },
              ].map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-ink/15 text-ink/70 hover:text-ink hover:border-gold transition-colors"
                  data-testid={`footer-social-${s.name.toLowerCase()}`}
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Services col */}
          <div className="lg:col-span-2">
            <h3 className="text-xs font-medium uppercase tracking-[0.22em] text-ink/50">
              {t("nav.services")}
            </h3>
            <ul className="mt-5 space-y-2.5 text-sm">
              {SERVICE_KEYS.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${lang}/services/${s.slug}`}
                    className="text-ink/75 hover:text-ink transition-colors"
                  >
                    {t(s.k)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company col */}
          <div className="lg:col-span-2">
            <h3 className="text-xs font-medium uppercase tracking-[0.22em] text-ink/50">
              {t("footer.company")}
            </h3>
            <ul className="mt-5 space-y-2.5 text-sm">
              {COMPANY_LINKS.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="text-ink/75 hover:text-ink transition-colors">
                    {t(l.k)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + Map col */}
          <div className="lg:col-span-4">
            <h3 className="text-xs font-medium uppercase tracking-[0.22em] text-ink/50">
              {t("footer.contact")}
            </h3>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <Mail className="mt-0.5 h-4 w-4 text-gold flex-shrink-0" />
                <a href={`mailto:${settings.brand_email}`} className="text-ink/85 hover:text-ink transition-colors" data-testid="footer-email">
                  {settings.brand_email}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Phone className="mt-0.5 h-4 w-4 text-gold flex-shrink-0" />
                <a href={`tel:${settings.brand_phone}`} className="text-ink/85 hover:text-ink transition-colors" data-testid="footer-phone">
                  {settings.brand_phone}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 text-gold flex-shrink-0" />
                <span className="text-ink/85 leading-relaxed">
                  {settings.brand_address}
                </span>
              </li>
            </ul>

            <div className="mt-5">
              <MapEmbed height={200} testId="footer-map" rounded="rounded-2xl" />
            </div>
          </div>
        </div>

        <div className="hairline mt-14" />

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-ink/55">
            © {year} Yastık Medya. {t("footer.rights")}
          </p>
          <div className="flex items-center gap-4">
            <Link to={`/${lang}/privacy`} className="text-xs text-ink/55 hover:text-ink transition-colors">
              {t("footer.privacy")}
            </Link>
            <Link to={`/${lang}/terms`} className="text-xs text-ink/55 hover:text-ink transition-colors">
              {t("footer.terms")}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      {/* SEO keyword zone — İstanbul semtleri ve ajans anahtar kelimeleri */}
      <div
        className="border-t border-mist/50 bg-cream"
        aria-hidden="true"
        data-testid="footer-seo-keywords"
      >
        <div className="container-page py-8">
          <p className="flex flex-wrap gap-x-3 gap-y-1.5 text-[0.65rem] text-ink/20 leading-relaxed select-none">
            {ISTANBUL_SEO_KEYWORDS.map((kw, i) => (
              <span key={i}>{kw}</span>
            ))}
          </p>
        </div>
      </div>
    </footer>
  );
}
